import React, { useState, useEffect } from 'react';
import axios from '../../axiosConfig';
import '../../styles/SchoolSettings.css';
import {useToast} from "../commons/toast/ToastContext";

const SchoolSettings = ({ schoolId }) => {
    const [settings, setSettings] = useState(null);
    const [formValues, setFormValues] = useState({
        minBookingDaysInAdvance: '',
        minDaysBeforeCancel: '',
        allowMultipleBookings: false,
        allowReserveList: false,
    });
    const [monthSelection, setMonthSelection] = useState([]); // Array of strings representing unlocked months
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const token = localStorage.getItem('jwt');
    const toast = useToast();

    // Define the 12 months as per Java Month enum
    const monthOptions = [
        { key: "JANUARY", label: "Styczeń" },
        { key: "FEBRUARY", label: "Luty" },
        { key: "MARCH", label: "Marzec" },
        { key: "APRIL", label: "Kwiecień" },
        { key: "MAY", label: "Maj" },
        { key: "JUNE", label: "Czerwiec" },
        { key: "JULY", label: "Lipiec" },
        { key: "AUGUST", label: "Sierpień" },
        { key: "SEPTEMBER", label: "Wrzesień" },
        { key: "OCTOBER", label: "Październik" },
        { key: "NOVEMBER", label: "Listopad" },
        { key: "DECEMBER", label: "Grudzień" }
    ];

    // Fetch current school settings on mount (or when schoolId changes)
    useEffect(() => {
        if (!schoolId) return;
        setLoading(true);
        axios.get(`/api/v1/school/${schoolId}/settings`, {
            headers: { Authorization: `Bearer ${token}` },
        })
            .then((res) => {
                const data = res.data;
                setSettings(data);
                setFormValues({
                    minBookingDaysInAdvance: data.minBookingDaysInAdvance || '',
                    minDaysBeforeCancel: data.minDaysBeforeCancel || '',
                    allowMultipleBookings: data.allowMultipleBookings || false,
                    allowReserveList: data.allowReserveList || false,
                });
                // unlockedMonths is a set; convert to array
                setMonthSelection(data.unlockedMonths ? Array.from(data.unlockedMonths) : []);
                setLoading(false);
            })
            .catch((error) => {
                toast.error('Nie udało się pobrać ustawień szkoły', 'Błąd pobierania');
                setLoading(false);
            });
    }, [schoolId, token]);

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormValues((prev) => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }));
    };

    const handleMonthToggle = (month) => {
        setMonthSelection((prev) => {
            if (prev.includes(month)) {
                return prev.filter((m) => m !== month);
            } else {
                return [...prev, month];
            }
        });
    };

    // Save general school settings (without months)
    const handleSaveSettings = () => {
        setSaving(true);
        const payload = {
            minBookingDaysInAdvance: Number(formValues.minBookingDaysInAdvance),
            minDaysBeforeCancel: Number(formValues.minDaysBeforeCancel),
            allowMultipleBookings: formValues.allowMultipleBookings,
            allowReserveList: formValues.allowReserveList,
        };
        axios.patch(`/api/v1/school/${schoolId}/settings`, payload, {
            headers: { Authorization: `Bearer ${token}` },
        })
            .then(() => {
                toast.success('Ustawienia szkoły zaktualizowane pomyślnie', 'Zapisano');
                setSaving(false);
            })
            .catch((error) => {
                toast.error('Nie udało się zaktualizować ustawień szkoły', 'Błąd zapisu');
                setSaving(false);
            });
    };

    // Unlock all months
    const handleUnlockAll = () => {
        setSaving(true);
        axios.patch(`/api/v1/school/${schoolId}/settings/unlock-all`, {}, {
            headers: { Authorization: `Bearer ${token}` },
        })
            .then(() => {
                toast.success('Wszystkie miesiące zostały odblokowane', 'Sukces');
                setMonthSelection(monthOptions.map(m => m.key));
                setSaving(false);
            })
            .catch((error) => {
                toast.error('Nie udało się odblokować wszystkich miesięcy', 'Błąd');
                setSaving(false);
            });
    };

    // Lock all months
    const handleLockAll = () => {
        setSaving(true);
        axios.patch(`/api/v1/school/${schoolId}/settings/lock-all`, {}, {
            headers: { Authorization: `Bearer ${token}` },
        })
            .then(() => {
                toast.success('Wszystkie miesiące zostały zablokowane', 'Sukces');
                setMonthSelection([]);
                setSaving(false);
            })
            .catch((error) => {
                toast.error('Nie udało się zablokować wszystkich miesięcy', 'Błąd');
                setSaving(false);
            });
    };

    // Apply changes to unlocked months
    const handleApplyMonthSettings = () => {
        setSaving(true);
        const currentUnlocked = settings?.unlockedMonths ? Array.from(settings.unlockedMonths) : [];
        const toUnlock = monthSelection.filter(m => !currentUnlocked.includes(m));
        const toLock = currentUnlocked.filter(m => !monthSelection.includes(m));

        const requests = [];
        if (toUnlock.length > 0) {
            requests.push(
                axios.patch(`/api/v1/school/${schoolId}/settings/unlock`, toUnlock, {
                    headers: { Authorization: `Bearer ${token}` },
                })
            );
        }
        if (toLock.length > 0) {
            requests.push(
                axios.patch(`/api/v1/school/${schoolId}/settings/lock`, toLock, {
                    headers: { Authorization: `Bearer ${token}` },
                })
            );
        }
        if (requests.length === 0) {
            toast.info('Brak zmian w ustawieniach miesięcy', 'Info');
            setSaving(false);
            return;
        }
        Promise.all(requests)
            .then(() => {
                toast.success('Ustawienia miesięcy zostały zaktualizowane', 'Zapisano');
                setSaving(false);
            })
            .catch((error) => {
                toast.error('Nie udało się zaktualizować ustawień miesięcy', 'Błąd');
                setSaving(false);
            });
    };

    if (loading) return <div className="loading">Ładowanie ustawień...</div>;
    if (!settings) return <div className="error-message">Nie udało się załadować ustawień</div>;

    return (
        <div className="school-settings-container">
            {/* General Settings Section */}
            <div className="settings-section settings-section-general">
                {/* Button positioned at top right */}
                <div className="settings-button-group">
                    <button className="settings-btn settings-btn-primary" onClick={handleSaveSettings} disabled={saving}>
                        {saving ? 'Zapisywanie...' : 'Zapisz ustawienia'}
                    </button>
                </div>

                <div className="settings-form-row">
                    <div className="settings-input-group">
                        <label>Możliwość rezerwacji treningu z wyprzedzeniem (ilość dni)</label>
                        <input
                            type="number"
                            name="minBookingDaysInAdvance"
                            value={formValues.minBookingDaysInAdvance}
                            onChange={handleInputChange}
                            className="settings-form-input"
                            placeholder="np. 7"
                        />
                    </div>
                    <div className="settings-input-group">
                        <label>Możliwa liczba dni przed, aby anulować trening</label>
                        <input
                            type="number"
                            name="minDaysBeforeCancel"
                            value={formValues.minDaysBeforeCancel}
                            onChange={handleInputChange}
                            className="settings-form-input"
                            placeholder="np. 1"
                        />
                    </div>
                </div>

                {/* Side-by-side checkboxes */}
                <div className="settings-checkbox-container">
                    <div className="settings-checkbox-group">
                        <label className="settings-checkbox-label">
                            <input
                                type="checkbox"
                                name="allowMultipleBookings"
                                checked={formValues.allowMultipleBookings}
                                onChange={handleInputChange}
                                className="settings-checkbox-input"
                            />
                            <span className="settings-checkbox-text">Pozwól na wiele zapisów</span>
                        </label>
                    </div>

                    <div className="settings-checkbox-group">
                        <label className="settings-checkbox-label">
                            <input
                                type="checkbox"
                                name="allowReserveList"
                                checked={formValues.allowReserveList}
                                onChange={handleInputChange}
                                className="settings-checkbox-input"
                            />
                            <span className="settings-checkbox-text">Pozwól na listę rezerwową</span>
                        </label>
                    </div>
                </div>
            </div>

            {/* Months Settings Section */}
            <div className="settings-section-title">Ustawienia miesięcy</div>

            <div className="settings-section settings-section-months">
                <div className="settings-months-grid">
                    {monthOptions.map((month) => (
                        <label key={month.key} className="settings-month-checkbox">
                            <input
                                type="checkbox"
                                checked={monthSelection.includes(month.key)}
                                onChange={() => handleMonthToggle(month.key)}
                                className="settings-month-checkbox-input"
                            />
                            <span className="settings-month-checkbox-text">{month.label}</span>
                        </label>
                    ))}
                </div>

                <div className="settings-button-group">
                    <button className="settings-btn settings-btn-primary" onClick={handleApplyMonthSettings} disabled={saving}>
                        {saving ? 'Zapisywanie...' : 'Zapisz ustawienia miesięcy'}
                    </button>

                    <div className="settings-months-toggle-group">
                        <label className="settings-months-toggle-label">
                            <input
                                type="checkbox"
                                checked={monthSelection.length === monthOptions.length}
                                onChange={(e) => {
                                    if (e.target.checked) {
                                        handleUnlockAll();
                                    }
                                }}
                                className="settings-months-toggle-input"
                                disabled={saving}
                            />
                            <span className="settings-months-toggle-text">Odblokuj wszystkie</span>
                        </label>
                    </div>

                    <div className="settings-months-toggle-group">
                        <label className="settings-months-toggle-label">
                            <input
                                type="checkbox"
                                checked={monthSelection.length === 0}
                                onChange={(e) => {
                                    if (e.target.checked) {
                                        handleLockAll();
                                    }
                                }}
                                className="settings-months-toggle-input"
                                disabled={saving}
                            />
                            <span className="settings-months-toggle-text">Zablokuj wszystkie</span>
                        </label>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SchoolSettings;